import { Dispatch, SetStateAction } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { SlickDataView, SlickGrid } from "slickgrid-react";
import { GanttStatic } from "./Gantt/dhtmlxgantt";
import type {
  CustomObject,
  DependencyAddEditData,
  DependencyData,
  GanttTask,
  Link,
  ResourceAddEditData,
  ResourceAssignment,
  Task,
  TaskLink,
  WarningState,
} from "@WebTypes/scheduleDomain";

/**
 * The contract record the Gantt is opened against. Contracts own their types; the chart only ever
 * reads these fields off it, so it is declared as the narrow view rather than re-exported whole.
 */
export interface GanttContractDetails {
  _id?: ObjectIdString;
  versionId?: ObjectIdString;
  userPermissions?: number[];
  [key: string]: unknown;
}

/** Change-order context threaded through the chart; only its presence/flags are read here. */
export type GanttChangeOrderState = Record<string, unknown> | null | undefined;

/** The zoom-level ref ("day" | "hour" | ...) the chart and its helpers share. */
export type GanttConfigRef = React.MutableRefObject<string>;

/**
 * The chart's currently-selected activity. It is `{}` whenever nothing is selected — the chart resets
 * it that way on load and on delete — so every field including `id` is optional here.
 */
export type SelectedGanttTask = Partial<GanttTask>;

/**
 * Ref surface the Schedule grids actually use. `SlickDataView.items`/`.selectedRowIds` and
 * `SlickGrid.selectedRows` are `protected` on the vendor classes; the grid code in this module
 * reaches all three directly, so they are re-declared here as public. Everything else is the real
 * SlickGrid1 ref.
 */
export type ScheduleGridRef<T extends GridRowBase> = SlickGrid1Ref<T> & {
  dataView: SlickDataView<T> & {
    items: T[];
    filteredItems: T[];
    selectedRowIds: Array<string | number>;
  };
  slickGrid: SlickGrid & { selectedRows: number[] };
};

export const GANTT_VIEW_TYPE = {
  PLANNED: "Planned",
  ACTUAL: "Actual",
  FORECAST: "Forecast",
  PLANNED_AGAINST_ACTUAL: "Planned Against Actual",
  PLANNED_AGAINST_ACTUAL_PERCENTAGE: "Planned Against Actual Percentage Based",
} as const;

export type GanttViewType = (typeof GANTT_VIEW_TYPE)[keyof typeof GANTT_VIEW_TYPE];

export interface GanttProps {
  refreshGanttContract?: boolean;
  setRefreshGanttContract?: (value: boolean) => void;
  activeTab?: string;
  viewType?: GanttViewType;
  changeOrderState?: GanttChangeOrderState;
  scheduleId?: ObjectIdString;
  spaceId?: ObjectIdString;
  projectId?: ObjectIdString;
  fileVersionId?: string;
  contractDetails?: GanttContractDetails;
  contractId?: ObjectIdString;
  attachTaskModalState?: (value: string, value1: boolean) => void;
  setDisableTabs?: (value: boolean) => void;
  showFullScreen?: boolean;
  setShowFullScreen?: (value: boolean) => void;
  fetchVersions?: () => void | Promise<void>;
  scheduleVersionId?: ObjectIdString;
}

export interface ResourceProps {
  scheduleId?: ObjectIdString;
  spaceId?: ObjectIdString;
  projectId?: ObjectIdString;
  fileVersionId?: ObjectIdString;
  showFullScreen?: boolean;
  setShowFullScreen?: (value: boolean) => void;
  fetchVersions?: () => void | Promise<void>;
  scheduleVersionId?: ObjectIdString;
}

export interface ToolbarProps {
  contractId?: string;
  scheduleId?: string;
  projectId?: string;
  spaceId?: string;
  enableImportButton: boolean;
  currentSelectedTask: SelectedGanttTask;
  setShowActivityInformationModal: (value: boolean) => void;
  inlineEditingEnabled: boolean;
  contractDetails: GanttContractDetails;
  changeOrderState: GanttChangeOrderState;
  setShowProgressModal: (value: boolean) => void;
  setCurrentSelectedTask: Dispatch<SetStateAction<SelectedGanttTask>>;
  setShowShimmer: (value: boolean) => void;
  setShowWarning: (value: WarningState) => void;
  setShowBoqModal: (value: boolean) => void;
  attachTaskModalState: (value: string, value1: boolean) => void;
  /** `indexNew`'s ref of task id → its predecessor links, kept in step with the chart's links. */
  taskIdAndPredecessorMapper: React.MutableRefObject<Record<string, DependencyData[]>>;
  ganttConfig: GanttConfigRef;
  setRerenderGantt: (value: boolean) => void;
  showDeleteActivityModal: boolean;
  setShowDeleteActivityModal: (value: boolean) => void;
  scheduleVersionId?: string;
  timezone: string;
  refreshParentSummaries: (
    taskId: string,
  ) => Array<{ id: string; progress?: number; plannedStartDate?: Date | null; plannedEndDate?: Date | null }>;
  saveBatchSummaryUpdates: (
    updates: Array<{ id: string; progress?: number; plannedStartDate?: Date | null; plannedEndDate?: Date | null }>,
  ) => Promise<void>;
  setSkipAfterTaskUpdate: (value: boolean) => void;
  isDeletingTask: React.MutableRefObject<boolean>;
  isGrouped?: boolean;
}

export interface ActivityInformationModalProps {
  showActivityInformationModal: boolean;
  loadingActivity?: boolean;
  setLoadingActivity: (value: boolean) => void;
  setShowActivityInformationModal: (value: boolean) => void;
  currentSelectedTask: SelectedGanttTask;
  setCurrentSelectedTask: Dispatch<SetStateAction<SelectedGanttTask>>;
  ganttConfig: GanttConfigRef;
  gantt: GanttStatic;
  /** `indexNew`'s running activity counter, used to seed a new activity's display id. */
  taskCounter: React.MutableRefObject<number>;
  scheduleId: ObjectIdString;
  fileVersionId?: string;
  spaceId?: ObjectIdString;
  projectId?: ObjectIdString;
}

export interface UpdatesModalProps {
  gantt: GanttStatic;
  scheduleId?: ObjectIdString;
  currentSelectedTask: SelectedGanttTask;
  setCurrentSelectedTask: Dispatch<SetStateAction<SelectedGanttTask>>;
  setShowProgressModal: Dispatch<SetStateAction<boolean>>;
  showProgressModal: boolean;
  loadingProgressModal?: boolean;
  setLoadingProgressModal: Dispatch<SetStateAction<boolean>>;
  ganttConfig?: GanttConfigRef;
}

export interface SimulationProps {
  activeTab: string;
  ganttDataAvailable: boolean;
  viewType: GanttViewType;
  setDisableTabs: (value: boolean) => void;
}

export interface GanttQueueItem {
  entity: string;
  action: string;
  /** The mutated Gantt payload, deep-cloned at enqueue time and posted verbatim. */
  data: Task | Link | ResourceAssignment | CustomObject;
}

export interface DateSliderProps {
  scheduleStartDate: number;
  scheduleEndDate: number;
  scheduleActualEndDate: number;
  scheduleActualStartDate: number;
  viewType: GanttViewType;
  scheduleId: string;
  onColumnChooserClick: () => void;
}

export interface DependencyProps {
  currentTab: string;
  gantt: GanttStatic;
  currentSelectedTask: SelectedGanttTask;
  deletedLinks: TaskLink[];
  setDeletedLinks: Dispatch<SetStateAction<TaskLink[]>>;
  dependencyAddEditData: DependencyAddEditData[];
  setDependencyAddEditData: Dispatch<SetStateAction<DependencyAddEditData[]>>;
  hoursPerDay: number;
}

/** NOT REFERENCED anywhere: kept as the declared shape of the resource tab's props. */
export interface ResourceGanttProps {
  deletedResources: ResourceAddEditData[];
  setDeletedResources: (value: ResourceAddEditData[]) => void;
  resourceAddEditData: ResourceAddEditData[];
  setResourceAddEditData: (value: ResourceAddEditData[]) => void;
  gantt: GanttStatic;
}
